import { BUILDER_DEFAULT_LOCATION_ID } from "../../theme";

const PROCUREIQ_SUPPLIER_COLORS = [
  "#1B4332", "#2D6A4F", "#3B82F6", "#7C3AED", "#E5533D", "#F59E0B", "#0D9488", "#DB2777",
];

export function procureiqDisplaySupplierName(name) {
  return String(name || "")
    .trim()
    .replace(/^\d{9,}\s*[-–]?\s+/, "");
}

/** Supy item names are often `{ en, ar }` — prefer English for display. */
export function procureiqDisplayProductName(name) {
  if (name == null || name === "") return "Item";
  if (typeof name === "object" && !Array.isArray(name)) {
    for (const key of ["en", "el", "gr", "ar"]) {
      const val = name[key];
      if (val != null && String(val).trim()) return String(val).trim();
    }
    for (const val of Object.values(name)) {
      if (val != null && String(val).trim()) return String(val).trim();
    }
    return "Item";
  }
  const text = String(name).trim();
  if (
    (text.startsWith("{") || text.startsWith("{'")) &&
    (text.includes("'en'") || text.includes('"en"'))
  ) {
    try {
      const parsed = JSON.parse(text.replace(/'/g, '"').replace(/\bNone\b/g, "null"));
      if (parsed && typeof parsed === "object") {
        return procureiqDisplayProductName(parsed);
      }
    } catch {
      /* keep raw */
    }
  }
  return text || "Item";
}

export function procureiqColorFromName(name) {
  let hash = 0;
  const s = procureiqDisplaySupplierName(name);
  for (let i = 0; i < s.length; i += 1) {
    hash = s.charCodeAt(i) + ((hash << 5) - hash);
  }
  return PROCUREIQ_SUPPLIER_COLORS[Math.abs(hash) % PROCUREIQ_SUPPLIER_COLORS.length];
}

export function procureiqSupplierInitial(name) {
  const cleaned = procureiqDisplaySupplierName(name);
  const match = cleaned.match(/[A-Za-zΑ-Ωα-ωΆ-ώ]/);
  return match ? match[0].toUpperCase() : "?";
}

export function procureiqFormatCutoffTime(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  const match = s.match(/(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return `${match[1].padStart(2, "0")}:${match[2]}`;
}

export function procureiqSupplierCutoffLabel(supplier) {
  const fromToday = procureiqFormatCutoffTime(supplier?.order_cutoff_today);
  if (fromToday) return fromToday;
  const slots = Array.isArray(supplier?.delivery_slots) ? supplier.delivery_slots : [];
  const dayNames = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  const todayName = dayNames[new Date().getDay()];
  const slot = slots.find(
    (s) => s && s.is_active !== false && String(s.cutoff_day || "").toLowerCase() === todayName
  );
  return procureiqFormatCutoffTime(slot?.cutoff_time);
}

/** House floor when supplier has no / zero configured min (Christos). */
export const PROCUREIQ_DEFAULT_MIN_ORDER_EUR = 20;

export function procureiqEffectiveMinOrderEur(minOrderEur) {
  if (minOrderEur == null) return PROCUREIQ_DEFAULT_MIN_ORDER_EUR;
  const n = Number(minOrderEur);
  if (!Number.isFinite(n) || n === 0) return PROCUREIQ_DEFAULT_MIN_ORDER_EUR;
  return n;
}

export function procureiqMinOrderApplies(minOrderEur) {
  return procureiqEffectiveMinOrderEur(minOrderEur) > 0;
}

export function procureiqEditedQtyForTotal(orderQtys, productId) {
  const raw = orderQtys[productId];
  if (raw === "" || raw == null) return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

export function procureiqEditedLineValue(line, orderQtys) {
  const qty = procureiqEditedQtyForTotal(orderQtys, line.product_id);
  const unitCost = Number(line.unit_cost);
  if (qty > 0 && Number.isFinite(unitCost)) return qty * unitCost;
  return 0;
}

export function procureiqSupplierLiveTotal(supplier, orderQtys) {
  let sum = 0;
  (supplier.lines || []).forEach((line) => {
    sum += procureiqEditedLineValue(line, orderQtys);
  });
  return Math.round(sum * 100) / 100;
}

export function procureiqSupplierIsBelowMin(supplier, orderQtys) {
  if (supplier.is_min_overridden) return false;
  if (!procureiqMinOrderApplies(supplier.min_order_eur)) return false;
  const total = procureiqSupplierLiveTotal(supplier, orderQtys);
  // Empty baskets are not sent and must not block Send All.
  if (total <= 0) return false;
  return total < procureiqEffectiveMinOrderEur(supplier.min_order_eur);
}

/** True when the supplier has something useful to show (order qty, suggest > 0, or needs count). */
export function procureiqSupplierHasActionableWork(supplier, orderQtys) {
  if (procureiqSupplierLiveTotal(supplier, orderQtys) > 0) return true;
  return (supplier.lines || []).some((line) => {
    if (line.needs_stock_count) return true;
    const suggested = Number(line.suggested_order_qty);
    return Number.isFinite(suggested) && suggested > 0;
  });
}

/** Days of stock left at the forecast daily rate. Items with no forecast sort last. */
export function procureiqDaysOfCover(line) {
  const stock = Number(line?.estimated_stock);
  const daily = Number(line?.predicted_daily_qty);
  if (!Number.isFinite(daily) || daily <= 0) return Number.POSITIVE_INFINITY;
  if (!Number.isFinite(stock) || stock <= 0) return 0;
  return stock / daily;
}

/** Soonest stockout first. Same cover: higher upcoming predicted need first. */
export function procureiqCompareBySoonestStockout(a, b) {
  const coverDiff = procureiqDaysOfCover(a) - procureiqDaysOfCover(b);
  if (coverDiff !== 0) return coverDiff;
  return (Number(b?.predicted_window_qty) || 0) - (Number(a?.predicted_window_qty) || 0);
}

export function procureiqDisplayBaseUnit(name) {
  const s = String(name || "").trim();
  if (/^pieces?$/i.test(s)) return "PC";
  return s;
}

/** Pack contents after a volume forecast was turned into bottles (btl). Piece stays PC. */
export function procureiqContentsUnit(baseUnitName, forecastUnit) {
  const base = String(baseUnitName || "").trim().toLowerCase();
  const forecast = String(forecastUnit || "").trim().toLowerCase();
  const countedAsPieces = /^(pc|piece|pieces)$/.test(forecast) || forecast.includes("piece");
  if (countedAsPieces && /^(ml|l|millilit(?:er|re)s?|lit(?:er|re)s?)$/.test(base)) {
    return "btl";
  }
  return procureiqDisplayBaseUnit(baseUnitName || (countedAsPieces ? "Piece" : ""));
}

export function procureiqPackageMenuLabel(pkg) {
  const unit = String(pkg?.unit_name || "").trim();
  const name = String(pkg?.package_name || "").trim();
  if (unit && name.toLowerCase().startsWith(unit.toLowerCase())) return name;
  return [unit, name].filter(Boolean).join(" ") || String(pkg?.label || "");
}

/** Whole packs needed to cover a base-unit quantity (pieces, mL, grams). */
export function procureiqPacksForNeed(baseNeed, unitsPerPack) {
  const need = Number(baseNeed);
  const units = Number(unitsPerPack);
  if (!Number.isFinite(need) || need <= 0 || !Number.isFinite(units) || units <= 0) return 0;
  return Math.ceil(need / units - 1e-9);
}

export function procureiqInitialOrderQty(suggestedOrderQty) {
  if (suggestedOrderQty == null) return "";
  return suggestedOrderQty;
}

export function procureiqBaseUnit(caseUnit) {
  if (!caseUnit) return "";
  const s = String(caseUnit).trim();
  if (/\bKIB\b/i.test(s)) return "KIB";
  // No leading \b — units often sit after digits ("0.1kg", "9x1L", "(9x1L)")
  const unitToken = s.match(/(Kg|kg|L|l|ml|gr|τμχ|ΤΜΧ|τεμ|pcs?|Piece|Pieces)\b/i);
  if (unitToken) {
    const lower = String(unitToken[1]).toLowerCase();
    if (lower === "kg") return "Kg";
    if (lower === "l") return "L";
    if (lower === "piece" || lower === "pieces" || lower === "pc" || lower === "pcs") {
      return "pc";
    }
    return unitToken[1];
  }
  const beforeParen = s.split("(")[0].trim();
  if (beforeParen && beforeParen.length <= 10) {
    const trailing = beforeParen.match(/([A-Za-zΑ-Ωα-ωμΜ]+)\s*$/);
    if (trailing) return trailing[1];
  }
  if (s.length > 8) return "";
  return s;
}

/** Pieces are counted, so the screen shows a whole number (11.8pc → 12pc). */
export function procureiqIsPieceUnit(caseUnit) {
  const unit = procureiqBaseUnit(caseUnit).toLowerCase();
  return unit === "pc" || unit === "τμχ" || unit === "τεμ";
}

function formatQtyNumber(n, caseUnit, decimals) {
  if (procureiqIsPieceUnit(caseUnit)) return String(Math.round(n));
  if (decimals != null) return n.toFixed(decimals);
  if (Number.isInteger(n) || Math.abs(n - Math.round(n)) < 1e-6) return String(Math.round(n));
  return n.toFixed(1);
}

export function procureiqFormatStockDisplay(estimatedStock, caseUnit) {
  const n = Number(estimatedStock);
  if (!Number.isFinite(n)) return null;
  const unit = procureiqBaseUnit(caseUnit);
  // Negative book stock is an empty shelf. Show 0, not −137.
  if (n < 0) return `0${unit}`;
  const num = procureiqIsPieceUnit(caseUnit)
    ? String(Math.round(n))
    : formatTrimmedDecimals(n, 3);
  return `${num}${unit}`;
}

/** 100.00 → "100". 10.50 → "10.50". 10.500 with max 3 → "10.5". */
function formatTrimmedDecimals(n, maxDecimals) {
  const fixed = n.toFixed(maxDecimals);
  return fixed.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
}

/** Format a qty for Suggest/Avg display, e.g. "8Kg" or "36pc". */
export function procureiqFormatQtyWithUnit(qty, caseUnit, { decimals = null } = {}) {
  const n = Number(qty);
  if (!Number.isFinite(n)) return "—";
  return `${formatQtyNumber(n, caseUnit, decimals)}${procureiqBaseUnit(caseUnit)}`;
}

/**
 * Clarify pack vs base units for week-order / analysis lines.
 * When units_per_pack > 1, suggested_order_qty is packs (e.g. 1), not pieces.
 * Returns { primary, secondary } for a two-line UI, or primary only.
 */
export function procureiqFormatOrderQtyParts(line, qty = line?.suggested_order_qty) {
  const n = Number(qty);
  if (!Number.isFinite(n) || n <= 0) {
    return { primary: n === 0 ? "0" : "—", secondary: null, packs: null, pieces: null };
  }
  const selectedPackage =
    (line?.packages || []).find((pkg) => pkg.packaging_id === line?.selected_package_id) ||
    (line?.packages || [])[0];
  const unitsPerPack = Number(
    selectedPackage?.units_per_pack ?? line?.units_per_pack
  );
  const forecastUnit = line?.forecast_unit || line?.case_unit || "";
  const contentsUnit = procureiqContentsUnit(
    selectedPackage?.baseunit_name ||
      (Number.isFinite(unitsPerPack) && unitsPerPack > 1 ? "Piece" : forecastUnit),
    forecastUnit
  );
  const packUnitName = String(
    selectedPackage?.unit_name ||
      (String(line?.order_unit || "").trim() &&
      !/^[×(]/.test(String(line.order_unit).trim())
        ? line.order_unit
        : "") ||
      ""
  ).trim();

  if (Number.isFinite(unitsPerPack) && unitsPerPack > 1) {
    const packs =
      Math.abs(n - Math.round(n)) < 1e-6 ? Math.round(n) : Math.round(n * 100) / 100;
    const piecesRaw = packs * unitsPerPack;
    const piecesLabel =
      Math.abs(piecesRaw - Math.round(piecesRaw)) < 1e-6
        ? String(Math.round(piecesRaw))
        : formatTrimmedDecimals(piecesRaw, 3);
    const packSize =
      Math.abs(unitsPerPack - Math.round(unitsPerPack)) < 1e-6
        ? String(Math.round(unitsPerPack))
        : formatTrimmedDecimals(unitsPerPack, 3);
    const packWord = packs === 1 ? "pack" : "packs";
    return {
      primary: `${packs} ${packWord}`,
      secondary: `× ${packSize} ${contentsUnit || "PC"} = ${piecesLabel} ${
        contentsUnit || "PC"
      }`.trim(),
      packs,
      pieces: piecesRaw,
      units_per_pack: unitsPerPack,
      pack_unit: packUnitName || null,
    };
  }

  return {
    primary: procureiqFormatQtyWithUnit(n, forecastUnit),
    secondary: null,
    packs: null,
    pieces: n,
    units_per_pack: null,
    pack_unit: null,
  };
}

export function procureiqAvgWindowQty(line) {
  if (line?.avg_window_qty != null && Number.isFinite(Number(line.avg_window_qty))) {
    return Number(line.avg_window_qty);
  }
  const avgDaily = Number(line?.avg_daily_qty_4w);
  const days = Number(line?.days_window) || 1;
  if (Number.isFinite(avgDaily)) return avgDaily * days;
  const predicted = Number(line?.predicted_window_qty);
  return Number.isFinite(predicted) ? predicted : null;
}

export function procureiqSupplierInfoTitle(supplier) {
  const cutoff = procureiqSupplierCutoffLabel(supplier);
  const min = formatProcureEuro(procureiqEffectiveMinOrderEur(supplier?.min_order_eur));
  const schedule =
    supplier?.delivery_schedule_summary ||
    (Number(supplier?.lead_days) > 0 ? `Lead ${Number(supplier.lead_days)}d` : null);
  const contact = [supplier?.contact_name, supplier?.phone].filter(Boolean).join(" ");
  const parts = [];
  if (cutoff) parts.push(`Cutoff ${cutoff}`);
  parts.push(`Min ${min}`);
  if (schedule) parts.push(schedule);
  if (contact) parts.push(contact);
  return parts.join(" · ");
}

function formatEuroNumber(n) {
  if (Math.abs(n - Math.round(n)) < 0.005) return String(Math.round(n));
  return n.toFixed(2);
}

export function formatProcureEuro(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "€0";
  return `€${formatEuroNumber(n)}`;
}

/** Compact euro for Bebas totals. Whole euros omit the decimal; cents stay at two places. */
export function formatProcureEuroCompact(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "€0";
  return `€${formatEuroNumber(n)}`;
}

export function procureiqFillPctColor(fillPct) {
  const v = Number(fillPct);
  if (!Number.isFinite(v)) return "#94A3B8";
  if (v < 0.5) return "#E5533D";
  if (v < 1) return "#F59E0B";
  return "#22C55E";
}

export async function procureiqApiError(res, fallback) {
  try {
    const body = await res.json();
    if (typeof body?.detail === "string") return body.detail;
  } catch {
    /* ignore */
  }
  return fallback;
}

export function procureiqIsDinasStore(storeName) {
  return String(storeName || "").trim().startsWith("DN");
}

export function procureiqBrandDotColor(storeName) {
  return procureiqIsDinasStore(storeName) ? "#F59E0B" : "var(--green)";
}

export function procureiqBrandLabel(storeName) {
  return procureiqIsDinasStore(storeName) ? "DiNAS" : "Protein Garden";
}

export function procureiqStoreWithBrandLabel(storeName) {
  return `${storeName} · ${procureiqBrandLabel(storeName)}`;
}

export function procureiqLocationKey(loc) {
  if (!loc) return null;
  const id = loc.unified_location_id;
  if (id == null || id === "") return null;
  const brand = loc.brand_id;
  if (brand == null || brand === "") return String(id);
  return `${id}:${brand}`;
}

/** Protein Garden. Used when a location is listed under more than one brand. */
const PROCUREIQ_DEFAULT_BRAND_ID = 15;

/** Resolve a picker key from URL/legacy location-only ids. Prefers brand 15 when ambiguous. */
export function procureiqResolveLocationKey(locs, locationId, brandId) {
  if (!Array.isArray(locs) || !locs.length) return null;
  if (locationId == null || locationId === "") return null;
  const locId = Number(locationId);
  const matches = locs.filter((loc) => Number(loc.unified_location_id) === locId);
  if (!matches.length) return null;
  if (brandId != null && brandId !== "") {
    const exact = matches.find((loc) => Number(loc.brand_id) === Number(brandId));
    if (exact) return procureiqLocationKey(exact);
  }
  const pg = matches.find((loc) => Number(loc.brand_id) === PROCUREIQ_DEFAULT_BRAND_ID);
  return procureiqLocationKey(pg || matches[0]);
}

/**
 * Picker default: location 71 + brand 15 when that pair is in active-locations.
 * Otherwise the first catalog row. Empty until the locations request returns.
 */
export function procureiqDefaultLocationKey(locs) {
  if (!Array.isArray(locs) || !locs.length) return null;
  const preferred = procureiqResolveLocationKey(
    locs,
    BUILDER_DEFAULT_LOCATION_ID,
    PROCUREIQ_DEFAULT_BRAND_ID
  );
  if (preferred) return preferred;
  return procureiqLocationKey(locs[0]);
}

/**
 * URL store, then the last store chosen on another page, then location 71 / brand 15.
 * When the URL names a location but no brand, keep the remembered brand for that location.
 */
export function procureiqLocationKeyFromSelection(locs, { locationId, brandId, remembered } = {}) {
  let brand = brandId;
  const sameRemembered = remembered
    && locationId != null
    && locationId !== ""
    && Number(remembered.locationId) === Number(locationId);
  if ((brand == null || brand === "") && sameRemembered) brand = remembered.brandId;
  const fromUrl = procureiqResolveLocationKey(locs, locationId, brand);
  if (fromUrl) return fromUrl;
  if (remembered) {
    const fromMemory = procureiqResolveLocationKey(locs, remembered.locationId, remembered.brandId);
    if (fromMemory) return fromMemory;
  }
  return procureiqDefaultLocationKey(locs);
}

export function procureiqLocationDotColor(loc) {
  if (!loc) return "var(--green)";
  const type = String(loc.location_type || "").toUpperCase();
  if (type === "KITCHEN") return "#94A3B8";
  if (type === "HUB") return "#94A3B8";
  if (Number(loc.brand_id) === 13) return "#F59E0B";
  if (Number(loc.brand_id) === 15) return "var(--green)";
  return "#94A3B8";
}

export function procureiqLocationWithBrandLabel(loc) {
  if (!loc) return "";
  const name = String(loc.location_name || "");
  const type = String(loc.location_type || "").toUpperCase();
  // Central kitchen (CK-*): location name only — not a store brand.
  if (type === "KITCHEN") return name;
  const brand =
    loc.brand_name ||
    (Number(loc.brand_id) === 13 ? "DiNAS" : Number(loc.brand_id) === 15 ? "Protein Garden" : "");
  return brand ? `${name} · ${brand}` : name;
}

export function procureiqFormatOrderHistoryDate(iso, lang) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const locale = lang === "gr" ? "el-GR" : "en-GB";
  const dayMonth = d.toLocaleDateString(locale, { day: "numeric", month: "short" });
  const time = d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${dayMonth} · ${time}`;
}
